import React, { Fragment, ReactNode, useEffect, useMemo, useState } from "react";
import { Container, Form, Spinner } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import {
  getEventArticleBySlug,
  type EventArticle
} from "../content/eventArticles";
import { AreaType } from "../enums/enum";
import { useLanguage } from "../i18n";
import { Layout } from "../Layout";
import { FullCalendarType } from "../model/FullCalendarType";
import { MatrixCalendarType } from "../model/MatrixCalendarType";
import {
  appIconUrl,
  appStoreQrCodeUrl,
  appStoreUrl,
  getSiteCopy,
  getStoreBadgeUrls,
  googlePlayQrCodeUrl,
  playStoreUrl
} from "../siteContent";
import Helper from "../utility/helper";
import { persistCalendarRegion, resolveSavedCalendarRegion } from "../utils/calendarRegion";
import { formatDateParam } from "../utils/dateFormat";
import { SakawiEventItem, toEventItem } from "../utils/eventViewModel";

export interface EventArticlePageProps {
  matrixSakawiNT: MatrixCalendarType[];
  matrixSakawiBT: MatrixCalendarType[];
  fullSakawiNT: FullCalendarType[];
  fullSakawiBT: FullCalendarType[];
}

const ensureCanonicalLink = () => {
  let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');

  if (!canonical) {
    canonical = document.createElement("link");
    canonical.rel = "canonical";
    document.head.appendChild(canonical);
  }

  return canonical;
};

const ensureMeta = (selector: string) => {
  const existing = document.querySelector<HTMLMetaElement>(selector);

  if (existing) {
    return existing;
  }

  const meta = document.createElement("meta");
  const nameMatch = selector.match(/meta\[name="([^"]+)"\]/);
  const propertyMatch = selector.match(/meta\[property="([^"]+)"\]/);

  if (nameMatch) {
    meta.name = nameMatch[1];
  } else if (propertyMatch) {
    meta.setAttribute("property", propertyMatch[1]);
  }

  document.head.appendChild(meta);
  return meta;
};

const setMetaContent = (selector: string, content: string) => {
  ensureMeta(selector).setAttribute("content", content);
};

const setArticleMetadata = (article: EventArticle, type: "article" | "website" = "article") => {
  document.title = article.seo.title;
  ensureCanonicalLink().href = article.seo.canonicalUrl;
  setMetaContent('meta[name="description"]', article.seo.description);
  setMetaContent('meta[property="og:title"]', article.seo.title);
  setMetaContent('meta[property="og:description"]', article.seo.description);
  setMetaContent('meta[property="og:url"]', article.seo.canonicalUrl);
  setMetaContent('meta[property="og:type"]', type);
  setMetaContent('meta[property="og:image"]', article.seo.imageUrl);
  setMetaContent('meta[name="twitter:card"]', "summary_large_image");
  setMetaContent('meta[name="twitter:title"]', article.seo.title);
  setMetaContent('meta[name="twitter:description"]', article.seo.description);
  setMetaContent('meta[name="twitter:image"]', article.seo.imageUrl);
};

function findUpcomingArticleEvents(
  article: EventArticle,
  matrixSakawi: MatrixCalendarType[],
  fullSakawi: FullCalendarType[]
) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const hamuTanranEvents = fullSakawi
    .filter((item) => item.dateAhier.ahierMonth.month === 5 && item.dateAhier.date === 29)
    .map((item) => ({
      eventType: "KatePaleiHamuTanran" as const,
      sakawiType: "sakawiAhier" as const,
      eventDate: item.dateGregory
    }));
  const yearEvents = [...Helper.getNextEvents(fullSakawi), ...hamuTanranEvents]
    .filter((event) => article.eventIds.includes(event.eventType))
    .map((event) => toEventItem(event, matrixSakawi, fullSakawi));

  return article.eventIds
    .map((eventId) => {
      const matching = yearEvents.filter((event) => event.eventType === eventId);
      return matching.find((event) => event.eventDate >= today) || matching[matching.length - 1];
    })
    .filter((event): event is SakawiEventItem => Boolean(event));
}

const renderInlineMarkdown = (text: string): ReactNode[] => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.filter(Boolean).map((part, index) => part.startsWith("**") && part.endsWith("**")
    ? <strong key={`${part}-${index}`}>{part.slice(2, -2)}</strong>
    : <Fragment key={`${part}-${index}`}>{part}</Fragment>);
};

const MarkdownArticle = ({ markdown }: { markdown: string }) => {
  const readerMarkdown = markdown
    .replace(/\r\n/g, "\n")
    .replace(/Verified (?:bibliographic )?URL:[ \t]*\n[ \t]*(https:\/\/[^\s]+)/g, "$1");
  const blocks = readerMarkdown.split(/\n\s*\n/);
  let passedTitle = false;
  let insideMajorSection = false;

  return <>
    {blocks.map((block, index) => {
      const text = block.replace(/\n/g, " ").trim();
      const heading = text.match(/^(#{1,2})\s+(.+)$/);
      if (heading) {
        if (!passedTitle) {
          passedTitle = true;
          return null;
        }
        const markdownLevel = heading[1].length;
        const Heading = markdownLevel === 1 || !insideMajorSection ? "h2" : "h3";
        if (markdownLevel === 1) insideMajorSection = true;
        return <Heading className="event-article-heading" key={text}>{renderInlineMarkdown(heading[2])}</Heading>;
      }

      if (/^Verified (bibliographic )?URL:$/.test(text) || text === "Do not invent a URL if none is already verified in the repository/task context.") {
        return null;
      }

      if (/^https:\/\//.test(text)) {
        return <p className="event-article-source-link" key={text}>
          <a href={text} target="_blank" rel="noopener noreferrer">Xem nguồn</a>
        </p>;
      }

      const numbered = text.match(/^(\d+)\.\s+([\s\S]+)$/);
      if (numbered) {
        return <div className="event-article-reference" key={`${numbered[1]}-${index}`}>
          <span aria-hidden="true">{numbered[1]}.</span>
          <p>{renderInlineMarkdown(numbered[2])}</p>
        </div>;
      }

      if (text.startsWith("**Sakawi mang tính tham khảo.")) {
        return <aside className="event-article-disclaimer" key={`${text}-${index}`}>
          <p>{renderInlineMarkdown(text)}</p>
        </aside>;
      }

      return <p key={`${text}-${index}`}>{renderInlineMarkdown(text)}</p>;
    })}
  </>;
};

function countdownText(copy: ReturnType<typeof getSiteCopy>, event: SakawiEventItem) {
  if (event.timing === "today") {
    return copy.events.countdownToday;
  }

  if (event.timing === "past") {
    return copy.events.countdownPast.replace("{count}", String(Math.abs(event.daysFromToday)));
  }

  if (event.daysFromToday === 1) {
    return copy.events.countdownTomorrow;
  }

  return copy.events.countdownFuture.replace("{count}", String(event.daysFromToday));
}

export const EventArticlePage = (props: EventArticlePageProps) => {
  const { articleSlug } = useParams<{ articleSlug: string }>();
  const { language } = useLanguage();
  const copy = getSiteCopy(language);
  const article = getEventArticleBySlug(language, articleSlug);
  const [areaType, setAreaType] = useState<AreaType>(resolveSavedCalendarRegion);
  const locale = language === "vi" ? "vi-VN" : "en-US";
  const storeBadgeUrls = getStoreBadgeUrls(language);

  const matrixSakawi = areaType === "NinhThuan" ? props.matrixSakawiNT : props.matrixSakawiBT;
  const fullSakawi = areaType === "NinhThuan" ? props.fullSakawiNT : props.fullSakawiBT;
  const articleEvents = useMemo(
    () => article ? findUpcomingArticleEvents(article, matrixSakawi, fullSakawi) : [],
    [article, fullSakawi, matrixSakawi]
  );

  useEffect(() => {
    if (article) {
      setArticleMetadata(article);
      return;
    }

    document.title = copy.events.articleNotFoundTitle;
    ensureCanonicalLink().href = "https://www.sakawi.com/events";
    setMetaContent('meta[name="description"]', copy.metadata.notFoundDescription);
    setMetaContent('meta[property="og:title"]', copy.events.articleNotFoundTitle);
    setMetaContent('meta[property="og:description"]', copy.metadata.notFoundDescription);
    setMetaContent('meta[property="og:url"]', "https://www.sakawi.com/events");
    setMetaContent('meta[property="og:type"]', "website");
    setMetaContent('meta[name="twitter:title"]', copy.events.articleNotFoundTitle);
    setMetaContent('meta[name="twitter:description"]', copy.metadata.notFoundDescription);
  }, [article, copy.events.articleNotFoundTitle, copy.metadata.notFoundDescription]);

  useEffect(() => {
    persistCalendarRegion(areaType);
  }, [areaType]);

  if (!article) {
    return (
      <Layout>
        <Container className="page-container public-page event-article-page">
          <nav className="breadcrumb-nav" aria-label={copy.accessibility.breadcrumbLabel}>
            <Link to="/">{copy.nav.home}</Link>
            <span aria-hidden="true">/</span>
            <Link to="/events">{copy.nav.events}</Link>
            <span aria-hidden="true">/</span>
            <span>{copy.events.articleNotFoundTitle}</span>
          </nav>
          <p className="page-eyebrow">{copy.events.eventKnowledge}</p>
          <h1>{copy.events.articleNotFoundTitle}</h1>
          <p className="page-lede">{copy.events.articleNotFoundText}</p>
          <div className="public-link-row">
            <Link to="/events">{copy.events.backToEvents}</Link>
          </div>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container className="page-container public-page event-article-page">
        <nav className="breadcrumb-nav" aria-label={copy.accessibility.breadcrumbLabel}>
          <Link to="/">{copy.nav.home}</Link>
          <span aria-hidden="true">/</span>
          <Link to="/events">{copy.nav.events}</Link>
          <span aria-hidden="true">/</span>
          <span>{article.shortTitle}</span>
        </nav>

        <p className="page-eyebrow">{copy.events.eventKnowledge}</p>
        <h1>{article.title}</h1>
        {article.summary && <p className="page-lede">{article.summary}</p>}
        <p><Link className="document-back-link" to="/events">{copy.events.backToEvents}</Link></p>

        <article className="event-article-body">
          {article.introduction.map((paragraph) => (
            <p className="document-paragraph" key={paragraph}>{paragraph}</p>
          ))}

          {fullSakawi.length === 0 ? (
            <div className="event-article-current event-article-loading" aria-live="polite">
              <Spinner animation="border" size="sm" />
            </div>
          ) : articleEvents.length > 0 ? (
            <section className="event-article-current" aria-labelledby="event-current-title">
              <div>
                <p className="page-eyebrow">{copy.events.currentEventInfo}</p>
                <h2 id="event-current-title">{copy.events.currentEventTitle}</h2>
                <p>{copy.events.currentEventDescription.replace("{region}", areaType === "NinhThuan" ? copy.events.ninhThuan : copy.events.binhThuan)}</p>
              </div>
              <Form className="event-article-region-form">
                <Form.Label className="area-selector-label">{copy.events.regionLabel}</Form.Label>
                <div className="area-selector" role="radiogroup" aria-label={copy.events.regionLabel}>
                  <Form.Check
                    id="event-article-region-ninh-thuan"
                    inline
                    type="radio"
                    label={copy.events.ninhThuan}
                    name="event-article-region"
                    checked={areaType === "NinhThuan"}
                    onChange={() => setAreaType("NinhThuan")}
                  />
                  <Form.Check
                    id="event-article-region-binh-thuan"
                    inline
                    type="radio"
                    label={copy.events.binhThuan}
                    name="event-article-region"
                    checked={areaType === "BinhThuan"}
                    onChange={() => setAreaType("BinhThuan")}
                  />
                </div>
              </Form>
              <div className="event-article-milestones">
                {articleEvents.map((event) => (
                  <section className="event-article-milestone" key={event.eventType}>
                    <h3>{copy.events.names[event.eventType]}</h3>
                    <p>
                      <time dateTime={formatDateParam(event.eventDate)}>
                        {event.eventDate.toLocaleDateString(locale, { year: "numeric", month: "long", day: "numeric" })}
                      </time>
                      {" · "}<strong className={`event-status event-status-${event.timing}`}>{countdownText(copy, event)}</strong>
                    </p>
                    <Link className="event-dialog-link" to={`/calendar?date=${formatDateParam(event.eventDate)}`}>
                      {copy.events.viewEventInCalendar}
                    </Link>
                  </section>
                ))}
              </div>
            </section>
          ) : null}

          {article.approvedMarkdown && <MarkdownArticle markdown={article.approvedMarkdown} />}

          {!article.approvedMarkdown && article.sections.map((section, index) => (
            <section className="document-section" aria-labelledby={`event-article-section-${index}`} key={section.heading}>
              <h2 id={`event-article-section-${index}`}>{section.heading}</h2>
              {section.body.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </section>
          ))}

          <section className="download-section event-article-download" aria-labelledby="event-article-download-title">
            <div className="download-section-heading">
              <img className="hero-app-icon" src={appIconUrl} alt={copy.accessibility.appIconAlt} width="96" height="96" />
              <div>
                <h2 id="event-article-download-title">{copy.events.followEventOnSakawi}</h2>
                <p>{copy.events.downloadEventCta}</p>
              </div>
            </div>
            <div className="event-article-store-links">
              <a href={appStoreUrl} target="_blank" rel="noopener noreferrer" aria-label={copy.home.appStoreBadgeAlt}>
                <img className="store-badge store-badge-app-store" src={storeBadgeUrls.appStore} alt={copy.home.appStoreBadgeAlt} />
              </a>
              <a href={playStoreUrl} target="_blank" rel="noopener noreferrer" aria-label={copy.home.googlePlayBadgeAlt}>
                <img className="store-badge store-badge-google-play" src={storeBadgeUrls.googlePlay} alt={copy.home.googlePlayBadgeAlt} />
              </a>
              <a className="qr-card" href={appStoreUrl} target="_blank" rel="noopener noreferrer" aria-label={copy.home.appStoreQrAlt}>
                <img src={appStoreQrCodeUrl} alt={copy.home.appStoreQrAlt} width="160" height="160" />
              </a>
              <a className="qr-card" href={playStoreUrl} target="_blank" rel="noopener noreferrer" aria-label={copy.home.googlePlayQrAlt}>
                <img src={googlePlayQrCodeUrl} alt={copy.home.googlePlayQrAlt} width="160" height="160" />
              </a>
            </div>
          </section>

          {!article.approvedMarkdown && <aside className="notice public-notice document-source-note event-article-sources" aria-labelledby="event-article-sources-title">
            <h2 id="event-article-sources-title">{copy.events.sourcesTitle}</h2>
            <ul>
              {article.sources.map((source) => (
                <li key={source.label}>
                  {source.href ? <Link to={source.href}>{source.label}</Link> : <span>{source.label}</span>}
                  {source.note && <span>{` - ${source.note}`}</span>}
                </li>
              ))}
            </ul>
            <p>{copy.events.editorialReviewNote}</p>
          </aside>}
        </article>
      </Container>
    </Layout>
  );
};
